import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { AccessWindowProps, ReportInfoProps } from "../../types/settings";

export interface AppSettingsState {
  isDeletePinRequired: boolean;
  isDeletePinSet: boolean;
  accessControlEnabled: boolean;
  accessWindows: AccessWindowProps[];
  reportInfo: ReportInfoProps;
  recordDaysEnabled: boolean;
  recordDays: number | null;
  sizeUnit: string;
  loading: boolean;
  error: string | null;
}

const initialReportInfo: ReportInfoProps = {
  companyName: "",
  address1: "",
  address2: "",
  tagline: "",
  titleType: "name",
  logo: null,
  watermark: null,
};

const initialState: AppSettingsState = {
  isDeletePinRequired: false,
  isDeletePinSet: false,
  accessControlEnabled: false,
  accessWindows: [],
  reportInfo: initialReportInfo,
  recordDaysEnabled: false,
  recordDays: null,
  sizeUnit: "mm",
  loading: false,
  error: null,
};

export const fetchAppSettingsData = createAsyncThunk(
  "fetchAppSettingsData",
  async () => {
    let response: any;
    await GET(`/v1/admin/settings`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const appSettingsSlice = createSlice({
  name: "appSettings",
  initialState,
  reducers: {
    setDeleteSettings: (
      state,
      action: PayloadAction<{
        isDeletePinRequired: boolean;
        isDeletePinSet: boolean;
      }>
    ) => {
      state.isDeletePinRequired = action.payload.isDeletePinRequired;
      state.isDeletePinSet = action.payload.isDeletePinSet;
    },
    setAccessControl: (
      state,
      action: PayloadAction<{
        accessControlEnabled: boolean;
        accessWindows: AccessWindowProps[];
      }>
    ) => {
      state.accessControlEnabled = action.payload.accessControlEnabled;
      state.accessWindows = action.payload.accessWindows;
    },
    setReportInfo: (state, action: PayloadAction<ReportInfoProps>) => {
      state.reportInfo = action.payload;
    },
    setRecordDays: (
      state,
      action: PayloadAction<{
        recordDaysEnabled: boolean;
        recordDays: number | null;
      }>
    ) => {
      state.recordDaysEnabled = action.payload.recordDaysEnabled;
      state.recordDays = action.payload.recordDays;
    },
    setSizeUnit: (state, action: PayloadAction<string>) => {
      state.sizeUnit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAppSettingsData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAppSettingsData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.isDeletePinRequired =
          action.payload.data.data.isDeletePinRequired;
        state.isDeletePinSet = action.payload.data.data.isDeletePinSet;
        state.accessControlEnabled =
          action.payload.data.data.accessControlEnabled;
        state.accessWindows = action.payload.data.data.accessWindows || [];
        state.reportInfo =
          action.payload.data.data.reportInfo || initialReportInfo;
        state.recordDaysEnabled = !!action.payload.data.data.recordDaysEnabled;
        const recordDays = Number(action.payload.data.data.recordDays);
        state.recordDays = recordDays > 0 ? recordDays : null;
        state.sizeUnit = action.payload.data.data.sizeUnit || "mm";
        state.error = null;
      } else {
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    });
    builder.addCase(fetchAppSettingsData.rejected, (state, action: any) => {
      state.loading = false;
      state.error =
        action?.payload?.data?.message ||
        "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
    });
  },
});

export const {
  setDeleteSettings,
  setAccessControl,
  setReportInfo,
  setRecordDays,
  setSizeUnit,
} = appSettingsSlice.actions;

export const getIsDeletePinRequired = (state: RootState) =>
  state.appSettings.isDeletePinRequired;
export const getIsDeletePinSet = (state: RootState) =>
  state.appSettings.isDeletePinSet;
export const getAccessControlEnabled = (state: RootState) =>
  state.appSettings.accessControlEnabled;
export const getAccessWindows = (state: RootState) =>
  state.appSettings.accessWindows;
export const getReportInfo = (state: RootState) =>
  state.appSettings.reportInfo;
export const getRecordDaysEnabled = (state: RootState) =>
  state.appSettings.recordDaysEnabled;
export const getRecordDays = (state: RootState) =>
  state.appSettings.recordDays;
export const getSizeUnit = (state: RootState) => state.appSettings.sizeUnit;
export const getAppSettingsLoading = (state: RootState) =>
  state.appSettings.loading;

export default appSettingsSlice.reducer;
